import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { APP_COMMIT_SHA, APP_VERSION } from "@shared/version";
import type { ReleaseNote } from "@shared/release-notes";

/**
 * Detecta quando o servidor está em uma versão diferente da que o cliente
 * carregou e expõe `updateAvailable` + `forceReload()` pro <UpdateBanner />.
 *
 * Como funciona:
 *  - O Vite inlina APP_COMMIT_SHA no bundle do cliente em build-time.
 *  - O servidor expõe GET /api/version com o SHA da build atualmente
 *    rodando em produção (também gerado em build-time).
 *  - A cada 60s (+ no focus/online), comparamos. Se diferente, marca
 *    updateAvailable=true e o banner aparece.
 *
 * Em dev (import.meta.env.DEV) ou quando algum dos lados retorna "dev",
 * desligamos a detecção — evita banner falso durante hot reload.
 */

const POLL_INTERVAL_MS = 60_000;
export const BEFORE_RELOAD_EVENT = "app:before-update-reload";

type VersionInfo = {
  version: string;
  commitSha: string;
  buildTime: number;
  /**
   * Resumo em linguagem de usuário do que a versão nova resolve. Vem do
   * servidor porque este bundle foi buildado antes dela existir. `null` em
   * versões sem nota cadastrada (e em servidores anteriores a esta mudança,
   * onde o campo simplesmente não vem).
   */
  releaseNote?: ReleaseNote | null;
};

type UpdateContextValue = {
  updateAvailable: boolean;
  serverVersion: VersionInfo | null;
  forceReload: () => Promise<void>;
};

const UpdateContext = createContext<UpdateContextValue | undefined>(undefined);

async function fetchServerVersion(): Promise<VersionInfo | null> {
  try {
    const res = await fetch("/api/version", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    return (await res.json()) as VersionInfo;
  } catch {
    return null;
  }
}

export function UpdateProvider({ children }: { children: React.ReactNode }) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState<VersionInfo | null>(null);
  const intervalRef = useRef<number | null>(null);

  const enabled = !import.meta.env.DEV && APP_COMMIT_SHA !== "dev";

  const check = useCallback(async () => {
    const info = await fetchServerVersion();
    if (!info) return;
    setServerVersion(info);
    if (info.commitSha && info.commitSha !== "dev" && info.commitSha !== APP_COMMIT_SHA) {
      setUpdateAvailable(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void check();
    intervalRef.current = window.setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => void check();
    const onOnline = () => void check();
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled, check]);

  const forceReload = useCallback(async () => {
    // 1. Avisa hooks de rascunho pra fazerem snapshot síncrono.
    window.dispatchEvent(new CustomEvent(BEFORE_RELOAD_EVENT));
    // 2. Aguarda 1 tick — listeners terminam de gravar no localStorage.
    await new Promise(resolve => setTimeout(resolve, 50));
    // 3. Limpa Cache API (Service Worker / fetch caches), se houver.
    try {
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch {
      // Cache API pode estar indisponível em alguns contextos — ignora.
    }
    // 4. sessionStorage some no reload de qualquer forma, mas limpamos
    //    explícito pra ficar previsível. localStorage fica intacto pros drafts.
    try { sessionStorage.clear(); } catch { /* noop */ }
    // 5. Reload com cache-bust no query string — força o navegador a
    //    revalidar mesmo se algum proxy intermediário cachear index.html.
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString());
    window.location.replace(url.toString());
  }, []);

  return (
    <UpdateContext.Provider
      value={{
        updateAvailable,
        serverVersion,
        forceReload,
      }}
    >
      {children}
    </UpdateContext.Provider>
  );
}

export function useUpdate(): UpdateContextValue {
  const ctx = useContext(UpdateContext);
  if (!ctx) {
    // Em ambientes sem provider (testes, storybook) retornamos um stub
    // inerte em vez de explodir — não queremos quebrar telas isoladas.
    return {
      updateAvailable: false,
      serverVersion: null,
      forceReload: async () => { /* noop sem provider */ },
    };
  }
  return ctx;
}

/** Versão da build do cliente (inlineada pelo Vite). */
export const CLIENT_VERSION = { version: APP_VERSION, commitSha: APP_COMMIT_SHA };
